import { Text } from '@mantine/core';
import { MonthPickerInput, YearPickerInput } from '@mantine/dates';
import dayjs from 'dayjs';
import * as React from 'react';

import { getMonthPickerIsoValue, getPeriodPickerBoundaryDate, getYearPickerIsoValue } from '@/utils/dateRange';

import s from './PeriodColumn.module.scss';

type PeriodColumnProps = {
  label: string;
  year: number | null;
  month: number | null;
  minYear?: number | null;
  minMonth?: number | null;
  maxYear?: number | null;
  maxMonth?: number | null;
  onYearChange: (year: number | null) => void;
  onMonthChange: (month: number | null) => void;
};

const PeriodColumn: React.FC<PeriodColumnProps> = ({
  label,
  year,
  month,
  minYear,
  minMonth,
  maxYear,
  maxMonth,
  onYearChange,
  onMonthChange,
}) => {
  const yearValue = getYearPickerIsoValue(year);
  const monthValue = getMonthPickerIsoValue(year, month);
  const minDate = getPeriodPickerBoundaryDate(minYear, minMonth ?? 0);
  const maxDate = getPeriodPickerBoundaryDate(maxYear, maxMonth ?? 11);

  const handleYearChange = React.useCallback(
    (value: string | null) => {
      if (value === null) {
        onYearChange(null);
        onMonthChange(null);

        return;
      }

      const parsedDate = dayjs(value);

      onYearChange(parsedDate.year());
    },
    [onMonthChange, onYearChange],
  );

  const handleMonthChange = React.useCallback(
    (value: string | null) => {
      if (value === null) {
        onMonthChange(null);

        return;
      }

      const parsedDate = dayjs(value);

      onYearChange(parsedDate.year());
      onMonthChange(parsedDate.month());
    },
    [onMonthChange, onYearChange],
  );

  return (
    <div className={s.root}>
      <Text size="xs" fw={600} c="dimmed" className={s.root__label}>
        {label}
      </Text>
      <YearPickerInput
        size="xs"
        placeholder="Год"
        value={yearValue}
        onChange={handleYearChange}
        valueFormat="YYYY"
        minDate={minDate}
        maxDate={maxDate}
        clearable
        popoverProps={{ withinPortal: false }}
        className={s.root__input}
      />
      <MonthPickerInput
        size="xs"
        placeholder="Месяц"
        value={monthValue}
        onChange={handleMonthChange}
        valueFormat="MMMM"
        maxLevel="year"
        minDate={minDate}
        maxDate={maxDate}
        clearable
        disabled={year === null}
        popoverProps={{ withinPortal: false }}
        className={s.root__input}
      />
    </div>
  );
};

export default PeriodColumn;
